/**
 * Upgrade Hero Component
 * Gradient hero section with social proof stats and wave divider
 */

import { Crown, Users, Package, Bell, TrendingDown } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { HERO_STATS } from '../utils/constants';
import type { HeroStat } from '../types';

// Icon mapping
const ICON_MAP = {
  users: Users,
  package: Package,
  bell: Bell,
  'trending-down': TrendingDown,
} as const;

interface StatCardProps {
  stat: HeroStat;
}

function StatCard({ stat }: StatCardProps) {
  const Icon = ICON_MAP[stat.icon];

  return (
    <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
      <div className={`w-10 h-10 rounded-lg ${stat.iconBg} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-xl font-bold text-white">{stat.value}</div>
        <div className="text-xs text-primary-100">{stat.label}</div>
      </div>
    </div>
  );
}

export function UpgradeHero() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-[35vh] overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      {/* Decorative blur circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
          <Crown className="w-4 h-4" />
          {t('upgrade.badge', 'Upgrade')}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
          {t('upgrade.heroTitle', 'Unlock Your')}{' '}
          <span className="text-accent">{t('upgrade.heroTitleHighlight', 'Full Potential')}</span>
        </h1>

        {/* Subtitle */}
        <p className="mt-4 text-lg text-primary-100 max-w-2xl animate-slide-up animation-delay-100">
          {t('upgrade.heroSubtitle', 'Track more products, get faster alerts, and access premium features')}
        </p>

        {/* Stats Row */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl animate-fade-in animation-delay-200">
          {HERO_STATS.map((stat) => (
            <StatCard key={stat.label} stat={stat} />
          ))}
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
          <path
            d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="#f9fafb"
          />
        </svg>
      </div>
    </section>
  );
}
